import React from 'react';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Cell } from 'recharts';
import { Trophy, MapPin } from 'lucide-react';

const cellCount = (batches, pred) => batches.reduce((s, b) => s + (b.cells || []).filter(pred).length, 0);

export const buildApiaryQueenStats = (batches) => {
  const groups = {};
  batches.forEach(b => {
    const key = b.yard_name || 'No apiary';
    (groups[key] = groups[key] || []).push(b);
  });
  return Object.entries(groups).map(([apiary, list]) => {
    const grafted = list.reduce((s, b) => s + (b.cells_grafted || 0), 0);
    const laying = cellCount(list, c => c.status === 'laying');
    return {
      apiary,
      batches: list.length,
      grafted,
      emerged: cellCount(list, c => ['emerged', 'mated', 'laying'].includes(c.status)),
      laying,
      failed: cellCount(list, c => c.status === 'failed'),
      successRate: grafted > 0 ? Math.round((laying / grafted) * 100) : 0
    };
  }).sort((a, b) => b.successRate - a.successRate || b.laying - a.laying);
};

export const ApiaryQueenComparison = ({ batches }) => {
  const stats = buildApiaryQueenStats(batches);
  if (stats.length === 0) return null;
  const best = stats[0].grafted > 0 && stats[0].successRate > 0 ? stats[0].apiary : null;
  return (
    <div data-testid="apiary-queen-comparison" className="bg-white rounded-lg border border-amber-100 p-4 mb-4">
      <h4 className="text-sm font-semibold text-amber-800 mb-1 flex items-center gap-2">
        <Trophy className="h-4 w-4 text-amber-600" /> Apiary Queen Comparison
      </h4>
      <p className="text-xs text-stone-500 mb-3">Success rate = queens laying ÷ cells grafted</p>
      <div className="grid md:grid-cols-2 gap-4">
        <div className="h-48">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={stats} layout="vertical" margin={{ left: 10, right: 20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f5f5f4" />
              <XAxis type="number" domain={[0, 100]} tickFormatter={v => `${v}%`} tick={{ fontSize: 11 }} />
              <YAxis type="category" dataKey="apiary" width={100} tick={{ fontSize: 11 }} />
              <Tooltip formatter={v => [`${v}%`, 'Success rate']} />
              <Bar dataKey="successRate" radius={[0, 6, 6, 0]}>
                {stats.map(s => <Cell key={s.apiary} fill={s.apiary === best ? '#d97706' : '#fcd34d'} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="space-y-2">
          {stats.map((s, i) => (
            <div key={s.apiary} data-testid={`apiary-queen-row-${i}`} className={`rounded-lg border p-3 ${s.apiary === best ? 'border-amber-400 bg-amber-50' : 'border-stone-200'}`}>
              <div className="flex items-center justify-between">
                <span className="font-medium text-stone-800 flex items-center gap-1"><MapPin className="h-3.5 w-3.5 text-teal-600" />{s.apiary}</span>
                <span className="flex items-center gap-2">
                  {s.apiary === best && <span data-testid="apiary-queen-best-badge" className="text-xs bg-amber-600 text-white px-2 py-0.5 rounded-full flex items-center gap-1"><Trophy className="h-3 w-3" />Best</span>}
                  <span className="text-lg font-bold text-amber-700">{s.successRate}%</span>
                </span>
              </div>
              <div className="text-xs text-stone-500 mt-1">
                {s.batches} batch{s.batches !== 1 ? 'es' : ''} · {s.grafted} grafted · {s.emerged} emerged · {s.laying} laying · {s.failed} failed
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
